/**
 * MODULE: domain/pages/frontend/components/DatabaseFieldCell.tsx
 * PURPOSE: Type-aware inline editor for database table cells
 */

import { useCallback } from 'react';
import { Badge, ToggleSwitch } from 'flowbite-react';
import { InlineEditableField } from '@shell/features/crm/components/shared';
import type { FieldDefinition } from '../../contracts/pages.types';
import {
  type CellSaveValue,
  fieldToInlineType,
  formatFieldDisplay,
  getLinkView,
  normalizePropertyValue,
  validateFieldDraft,
} from '../utils/databaseFieldUtils';

type Props = {
  field: FieldDefinition;
  value: unknown;
  onSave: (value: CellSaveValue) => void;
  readOnly?: boolean;
};

export function DatabaseFieldCell({ field, value, onSave, readOnly = false }: Props) {
  const normalized = normalizePropertyValue(field, value);
  const inlineType = fieldToInlineType(field);

  if (readOnly) {
    return (
      <span className="text-sm font-semibold text-gray-900 dark:text-white">
        {formatFieldDisplay(field, normalized)}
      </span>
    );
  }

  const handleInlineSave = useCallback(
    (next: string | number) => {
      onSave(next);
    },
    [onSave]
  );

  if (field.field_type === 'checkbox') {
    const checked = normalized === true;
    return (
      <div className="flex items-center py-1" data-testid={`pages-field-checkbox-${field.id}`}>
        <ToggleSwitch
          checked={checked}
          label={checked ? 'Yes' : 'No'}
          onChange={(next) => onSave(next)}
        />
      </div>
    );
  }

  if (field.field_type === 'multi_select') {
    const selected = Array.isArray(normalized) ? normalized : [];
    const toggleOption = (optionId: string) => {
      const next = selected.includes(optionId)
        ? selected.filter((id) => id !== optionId)
        : [...selected, optionId];
      onSave(next);
    };

    return (
      <div className="flex flex-wrap gap-1 py-1" data-testid={`pages-field-multi-${field.id}`}>
        {(field.options ?? []).map((option) => {
          const active = selected.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              className="rounded-full focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500"
              onClick={(e) => {
                e.stopPropagation();
                toggleOption(option.id);
              }}
              aria-pressed={active}
              data-testid={`pages-field-multi-${field.id}-${option.id}`}
            >
              <Badge color={active ? 'info' : 'gray'}>{option.name}</Badge>
            </button>
          );
        })}
        {!field.options?.length ? (
          <span className="text-sm text-gray-400">Add options in schema</span>
        ) : null}
      </div>
    );
  }

  if (field.field_type === 'created_at' || field.field_type === 'created_by') {
    return (
      <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
        {formatFieldDisplay(field, normalized)}
      </span>
    );
  }

  if (!inlineType) {
    return (
      <span className="text-sm font-semibold text-gray-900 dark:text-white">
        {formatFieldDisplay(field, normalized)}
      </span>
    );
  }

  const inlineValue: string | number =
    typeof normalized === 'boolean'
      ? String(normalized)
      : Array.isArray(normalized)
        ? normalized.join(',')
        : normalized;

  return (
    <InlineEditableField
      label=""
      fieldType={inlineType}
      value={inlineValue}
      options={field.options?.map((o) => ({ value: o.id, label: o.name }))}
      linkView={getLinkView(field)}
      validateDraft={(draft) => validateFieldDraft(field, draft)}
      formatDisplay={() => formatFieldDisplay(field, normalized)}
      onSave={handleInlineSave}
    />
  );
}
